import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ConfigurationService } from './config.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isLoggingOut = false;

	constructor(
		private readonly authService: AuthService,
		private readonly configService: ConfigurationService,
		private readonly messageService: MessageService,
		private readonly router: Router,
		private readonly zone: NgZone,
	) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const apiBaseUrl = this.configService.configuration.apiBaseUrl;

		if (!apiBaseUrl || !req.url.startsWith(apiBaseUrl)) {
			return next.handle(req);
		}

		return from(this.authService.getToken()).pipe(
			switchMap((token) => {
				const authReq = token
					? req.clone({
							setHeaders: { Authorization: `Bearer ${token}` },
					  })
					: req;
				return next.handle(authReq).pipe(
					catchError((error: HttpErrorResponse) => {
						// Gérer les erreurs 401 et 403 directement dans l'intercepteur
						if (error.status === 401 || error.status === 403) {
							this.handleUnauthorizedError(error.status);
						}
						// Propager l'erreur pour que les composants puissent réagir
						return throwError(() => error);
					}),
				);
			}),
		);
	}

	private handleUnauthorizedError(status: number) {
		// Éviter les déconnexions multiples
		if (this.isLoggingOut) {
			return;
		}
		this.isLoggingOut = true;

		const message =
			status === 401
				? 'Votre session a expiré. Vous allez être déconnecté dans quelques secondes.'
				: "Vous n'avez pas les autorisations nécessaires pour accéder à cette ressource. Vous allez être déconnecté dans quelques secondes.";

		// Afficher le message immédiatement en utilisant requestAnimationFrame pour s'assurer qu'il s'affiche avant le prochain rendu
		requestAnimationFrame(() => {
			this.zone.run(() => {
				this.messageService.add({
					severity: 'error',
					summary: status === 401 ? 'Session expirée' : 'Accès refusé',
					detail: message,
					life: 5000,
				});
			});
		});

		// Déconnecter après 5 secondes
		setTimeout(() => {
			this.zone.run(() => {
				this.authService.logout();
				this.router.navigate(['/login'], {});
			});
		}, 6000);
	}
}
